import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import SimpleApp from './components/SimpleApp';
import BuildCommercialApp from './components/BuildCommercialApp';
import ArtifactApp from './components/ArtifactApp';
import AppBuilderApp from './components/AppBuilderApp';
import PreorderApp from './components/PreorderApp';
import Login from './components/Login';
import { API_URL } from './config';

// Routes that are publicly accessible (no login required).
// The preorder page is opened by event attendees from a QR code, so it
// must not be gated behind the superpossible-key login.
const PUBLIC_PATH_PREFIXES = ['/preorder'];

const isPublicPath = (pathname: string): boolean =>
  PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

function App() {
  // Compute once per mount. We do not have client-side routing, so the path
  // is stable for the lifetime of this component.
  const path = window.location.pathname;
  const isPublic = isPublicPath(path);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status on app load (skipped for public pages).
  useEffect(() => {
    if (isPublic) {
      setIsLoading(false);
      return;
    }
    const checkCredentials = async () => {
      const savedSuperpossibleKey = localStorage.getItem('superpossible_key');
      console.log('savedSuperpossibleKey', savedSuperpossibleKey);
      if (!savedSuperpossibleKey) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/credentials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ superpossible_key: savedSuperpossibleKey }),
        });
        const data = await response.json(); 
        if (data && data.success === true) {
          setIsAuthenticated(true);
        } else {
          // Clear invalid key from localStorage to prevent loops
          localStorage.removeItem('superpossible_key');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking credentials:', error);
        // Clear invalid key from localStorage to prevent loops
        localStorage.removeItem('superpossible_key');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    checkCredentials();
  }, []);

  // Public routes (no login required) are served immediately.
  if (isPublic) {
    return <PreorderApp />;
  }

  if (isLoading) {
    return (
      <div className="ed-boot">
        <img src="/superpossible_logo.svg" alt="SuperPossible" />
        <span className="ed-spinner ed-spinner--lg" role="status" aria-label="Loading" />
      </div>
    );
  }

  const appContent = (() => {
    if (!isAuthenticated) {
      return <Login onLogin={() => setIsAuthenticated(true)} />;
    }
    if (path === '/commercial' || path.startsWith('/commercial/')) {
      return <BuildCommercialApp />;
    }
    if (path === '/artifact' || path.startsWith('/artifact/')) {
      return <ArtifactApp />;
    }
    if (path === '/appbuilder' || path.startsWith('/appbuilder/')) {
      return <AppBuilderApp />;
    }
    return <SimpleApp />;
  })();

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            borderRadius: 10,
            background: 'rgba(18, 24, 32, 0.94)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            color: '#EEF2F7',
            boxShadow: '0 18px 44px rgba(0, 0, 0, 0.55)',
          },
        }}
      />
      {appContent}
    </>
  );
}

export default App; 